import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type ThemeMode = 'system' | 'light' | 'dark'

interface DarkModeContextType {
  isDarkMode: boolean
  themeMode: ThemeMode
  setTheme: (mode: ThemeMode) => void
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined)

export function useDarkMode() {
  const context = useContext(DarkModeContext)
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider')
  }
  return context
}

interface DarkModeProviderProps {
  children: ReactNode
}

// Theme modes: 'system', 'light', 'dark'
export function DarkModeProvider({ children }: DarkModeProviderProps) {
  // Get initial theme mode from localStorage, default to 'system'
  // Also migrate from old 'darkMode' key if present
  const getInitialThemeMode = (): ThemeMode => {
    const stored = localStorage.getItem('themeMode')
    if (stored && (stored === 'system' || stored === 'light' || stored === 'dark')) {
      return stored as ThemeMode
    }
    // Migrate from old 'darkMode' key
    const oldDarkMode = localStorage.getItem('darkMode')
    if (oldDarkMode !== null) {
      const migratedMode = oldDarkMode === 'true' ? 'dark' : 'light'
      localStorage.setItem('themeMode', migratedMode)
      localStorage.removeItem('darkMode') // Clean up old key
      return migratedMode as ThemeMode
    }
    return 'system'
  }

  // Get the actual dark mode state based on theme mode
  const getEffectiveDarkMode = (mode: ThemeMode): boolean => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return mode === 'dark'
  }

  const initialThemeMode = getInitialThemeMode()
  const initialDarkMode = getEffectiveDarkMode(initialThemeMode)
  
  // Apply classes immediately on initialization to prevent flash
  if (typeof document !== 'undefined') {
    if (initialDarkMode) {
      document.documentElement.classList.add('dark-mode')
      document.documentElement.classList.remove('light-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
      document.documentElement.classList.add('light-mode')
    }
  }
  
  const [themeMode, setThemeMode] = useState<ThemeMode>(initialThemeMode)
  const [isDarkMode, setIsDarkMode] = useState<boolean>(initialDarkMode)

  // Listen to system preference changes when in system mode
  useEffect(() => {
    if (themeMode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      setIsDarkMode(mediaQuery.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode])

  // Update dark mode state when theme mode changes
  useEffect(() => {
    setIsDarkMode(getEffectiveDarkMode(themeMode))
  }, [themeMode])

  // Update document class and localStorage
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode')
      document.documentElement.classList.remove('light-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
      document.documentElement.classList.add('light-mode')
    }
    localStorage.setItem('themeMode', themeMode)
  }, [isDarkMode, themeMode])

  const setTheme = (mode: ThemeMode) => {
    setThemeMode(mode)
  }

  return (
    <DarkModeContext.Provider value={{ isDarkMode, themeMode, setTheme }}>
      {children}
    </DarkModeContext.Provider>
  )
}

